/**
 * Add-on Sensor (AOS) CrossFrame Listeners
 *
 * Handles CrossFrame requests for AOS operations across 3 backend services:
 * - Claire: entity graph queries (getEntity, getEntityRelationships, getEntityRelationshipsBatch, getModel)
 * - Danielle: fixture lifecycle (getFixturesByState, attachAddOnSensor, detachAddOnSensor)
 * - RedRackham: Aruba calibration (runArubaCalibration, getCalibrationRunStatus)
 *
 * Validates required parameters and delegates to the Gateway client via getServiceClient().
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'
import {
    GetEntityInput,
    GetEntityOutput,
    GetEntityRelationshipsInput,
    GetEntityRelationshipsOutput,
    GetEntityRelationshipsBatchInput,
    GetEntityRelationshipsBatchOutput,
    GetModelInput,
    GetModelOutput,
} from '@amzn/coral_com-amazon-ihmclaire'
import {
    GetFixturesByStateInput,
    GetFixturesByStateOutput,
    AttachAddOnSensorInput,
    DetachAddOnSensorInput,
} from '@amzn/coral_com-amazon-ihmdanielle'
import {
    RunArubaCalibrationInput,
    RunArubaCalibrationOutput,
    GetCalibrationRunStatusInput,
    GetCalibrationRunStatusOutput,
} from '@amzn/coral_com-amazon-ihmredrackham-coral'
import { validateRequired, validateRequiredArray } from './listenerUtils'

// --- Claire: entity graph operations ---

export async function getEntityListener (message: any): Promise<GetEntityOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'entityId')
    const client = getServiceClient()
    return client.getAddOnSensorEntity(GetEntityInput.clone(params)).toPromise()
}

export async function getEntityRelationshipsListener (message: any): Promise<GetEntityRelationshipsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'entityId')
    const client = getServiceClient()
    return client.getAddOnSensorRelationships(GetEntityRelationshipsInput.clone(params)).toPromise()
}

export async function getEntityRelationshipsBatchListener (message: any): Promise<GetEntityRelationshipsBatchOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    validateRequiredArray(params, 'entityIdsList')
    const client = getServiceClient()
    return client.getAddOnSensorRelationshipsBatch(GetEntityRelationshipsBatchInput.clone(params)).toPromise()
}

export async function getModelListener (message: any): Promise<GetModelOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'modelId')
    const client = getServiceClient()
    return client.getAddOnSensorModel(GetModelInput.clone(params)).toPromise()
}

// --- Danielle: fixture lifecycle operations ---

export async function getFixturesByStateListener (message: any): Promise<GetFixturesByStateOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'fixtureType')
    validateRequiredArray(params, 'lifecycleStates')
    const client = getServiceClient()
    return client.getAddOnSensorFixturesByState(GetFixturesByStateInput.clone(params)).toPromise()
}

export async function attachAddOnSensorListener (message: any): Promise<void> {
    const params = (message && message.data) || {}
    validateRequired(params, 'enclosureId', 'shelfId', 'merchandisingFixtureId')
    const client = getServiceClient()
    await client.attachAddOnSensor(AttachAddOnSensorInput.clone(params)).toPromise()
}

export async function detachAddOnSensorListener (message: any): Promise<void> {
    const params = (message && message.data) || {}
    validateRequired(params, 'enclosureId', 'shelfId', 'merchandisingFixtureId')
    const client = getServiceClient()
    await client.detachAddOnSensor(DetachAddOnSensorInput.clone(params)).toPromise()
}

// --- RedRackham: Aruba calibration operations ---

export async function runArubaCalibrationListener (message: any): Promise<RunArubaCalibrationOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    validateRequiredArray(params, 'arubaMappings')
    const client = getServiceClient()
    return client.runAddOnSensorCalibration(RunArubaCalibrationInput.clone(params)).toPromise()
}

export async function getCalibrationRunStatusListener (message: any): Promise<GetCalibrationRunStatusOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'runId', 'workflowName')
    const client = getServiceClient()
    return client.getAddOnSensorCalibrationStatus(GetCalibrationRunStatusInput.clone(params)).toPromise()
}
